import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  ArrowLeft,
  CalendarDays,
  ExternalLink,
  Search,
  SlidersHorizontal,
  X,
} from "lucide-react";

import {
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { useAuth } from "../../../../contexts/AuthContext";
import { getAccessToken } from "../../../../awsAuth";

import BookingPublic from "./BookingPublic";
import BusinessModuleCreate from "./BusinessModuleCreate";

import "./ServicesCatalogAll.scss";
import { photoVariant } from "../../../../utils/imageUrl";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "https://cvanto-api.onrender.com";

export default function ServicesCatalogAll() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userId } = useAuth();

  const viewedUserId =
    params.userId || userId;

  const [services, setServices] =
    useState([]);

  const [businessName, setBusinessName] =
    useState("Каталог послуг");

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [query, setQuery] =
    useState("");

  const [sort, setSort] =
    useState("newest");

  const [priceType, setPriceType] =
    useState("all");

  const [
    selectedBookingService,
    setSelectedBookingService,
  ] = useState(null);

  const bookingModalOpen =
    !!selectedBookingService;

  const isOwnProfile =
    !!userId &&
    !!viewedUserId &&
    String(userId) ===
      String(viewedUserId);

  const [editingItem, setEditingItem] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const [actionError, setActionError] = useState("");

  useEffect(() => {
    let alive = true;

    async function loadServices() {
      if (!viewedUserId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const token = await getAccessToken()
          .catch(() => null);

        const headers = token
          ? {
              Authorization:
                `Bearer ${token}`,
            }
          : {};

        const servicesUrl =
        isOwnProfile
          ? `${API_BASE}/api/business/services/me`
          : `${API_BASE}/api/business/services/user/${encodeURIComponent(
              viewedUserId
            )}`;

        const servicesRes = await fetch(
          servicesUrl,
          {
            headers,
          }
        );

        const servicesData =
          await servicesRes
            .json()
            .catch(() => ({}));

        if (!servicesRes.ok) {
          throw new Error(
            servicesData?.error ||
              "Не вдалося завантажити послуги"
          );
        }

        const profileRes = await fetch(
          `${API_BASE}/api/business/profile/${encodeURIComponent(
            viewedUserId
          )}`,
          {
            headers,
          }
        );

        const profileData =
          await profileRes
            .json()
            .catch(() => ({}));

        if (!alive) return;

        setServices(
          Array.isArray(
            servicesData?.items
          )
            ? servicesData.items
            : []
        );

        setBusinessName(
          profileData?.userBase
            ?.username ||
            profileData?.profile?.title ||
            profileData?.profile?.hero
              ?.title ||
            "Каталог послуг"
        );
      } catch (loadError) {
        console.error(
          "[ServicesCatalogAll] load error:",
          loadError
        );

        if (alive) {
          setError(
            loadError.message ||
              "Помилка завантаження"
          );
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    }

    loadServices();

    return () => {
      alive = false;
    };
  }, [
    viewedUserId,
    userId,
    isOwnProfile,
  ]);

  // Deep-link from ListingDetails.js's "Редагувати" for a business_service
  // (?edit=<listingId>): auto-open the same edit modal the card's own
  // "Редагувати" button already uses, once the matching item has loaded.
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId || editingItem) return;

    const match = services.find((s) => s.listingId === editId);
    if (match) setEditingItem(match);
  }, [searchParams, services, editingItem]);

  const closeEditModal = () => {
    setEditingItem(null);

    if (searchParams.has("edit")) {
      const next = new URLSearchParams(searchParams);
      next.delete("edit");
      setSearchParams(next, { replace: true });
    }
  };

  useEffect(() => {
    if (!bookingModalOpen) return;

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setSelectedBookingService(
          null
        );
      }
    };

    window.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [bookingModalOpen]);

  const filteredServices =
    useMemo(() => {
      let next = Array.isArray(
        services
      )
        ? [...services]
        : [];

      const normalizedQuery =
        query.trim().toLowerCase();

      if (normalizedQuery) {
        next = next.filter(
          (item) => {
            const text = [
              item.title || "",
              item.description || "",
              item.duration || "",
              item.location || "",
            ]
              .join(" ")
              .toLowerCase();

            return text.includes(
              normalizedQuery
            );
          }
        );
      }

      if (
        priceType === "withPrice"
      ) {
        next = next.filter(
          (item) =>
            Number(item.price || 0) >
            0
        );
      }

      if (
        priceType === "noPrice"
      ) {
        next = next.filter(
          (item) =>
            Number(item.price || 0) <=
            0
        );
      }

      if (sort === "priceAsc") {
        next.sort(
          (a, b) =>
            Number(a.price || 0) -
            Number(b.price || 0)
        );
      }

      if (sort === "priceDesc") {
        next.sort(
          (a, b) =>
            Number(b.price || 0) -
            Number(a.price || 0)
        );
      }

      if (sort === "oldest") {
        next.sort(
          (a, b) =>
            Number(a.createdAt || 0) -
            Number(b.createdAt || 0)
        );
      }

      if (sort === "newest") {
        next.sort(
          (a, b) =>
            Number(b.createdAt || 0) -
            Number(a.createdAt || 0)
        );
      }

      return next;
    }, [
      services,
      query,
      sort,
      priceType,
    ]);

  const formatPrice = (item) => {
    if (
      Number(item?.price || 0) <= 0
    ) {
      return "Ціну уточнюйте";
    }

    return `${item.price} ${
      item.currency || "ГРН"
    }`;
  };

  const openServiceDetails = (
    item
  ) => {
    const listingId =
      item?.listingId || item?.id;

    if (!listingId) return;

    navigate(
      `/listing/${encodeURIComponent(
        listingId
      )}`
    );
  };

  const openBooking = (item) => {
    if (!item) return;

    setSelectedBookingService(item);
  };

  const closeBooking = () => {
    setSelectedBookingService(null);
  };

  const authHeaders = async () => {
    const token = await getAccessToken().catch(() => null);
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleEditSaved = (updated) => {
    if (!updated?.listingId) return;
    setServices((prev) =>
      prev.map((s) => (s.listingId === updated.listingId ? { ...s, ...updated } : s))
    );
  };

  const toggleHidden = async (item) => {
    if (busyId) return;

    const hiding = (item.status || "active") !== "hidden";
    const action = hiding ? "hide" : "unhide";
    const serviceId = item.listingId || item.id;

    setBusyId(serviceId);
    setActionError("");

    try {
      const headers = await authHeaders();
      const res = await fetch(
        `${API_BASE}/api/business/services/${encodeURIComponent(serviceId)}/${action}`,
        { method: "PATCH", headers }
      );
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || "Не вдалося змінити видимість");
      }

      setServices((prev) =>
        prev.map((s) =>
          (s.listingId || s.id) === serviceId
            ? { ...s, status: hiding ? "hidden" : "active" }
            : s
        )
      );
    } catch (err) {
      setActionError(err.message || "Не вдалося змінити видимість");
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    const item = confirmDeleteItem;
    if (!item) return;

    const serviceId = item.listingId || item.id;

    setBusyId(serviceId);
    setActionError("");

    try {
      const headers = await authHeaders();
      const res = await fetch(
        `${API_BASE}/api/business/services/${encodeURIComponent(serviceId)}`,
        { method: "DELETE", headers }
      );
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || "Не вдалося видалити послугу");
      }

      setServices((prev) => prev.filter((s) => (s.listingId || s.id) !== serviceId));
    } catch (err) {
      setActionError(err.message || "Не вдалося видалити послугу");
    } finally {
      setBusyId(null);
      setConfirmDeleteItem(null);
    }
  };

  if (loading) {
    return (
      <main className="scAll">
        <div className="scAll__state">
          Завантаження послуг...
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="scAll">
        <div className="scAll__state scAll__state--error">
          {error}
        </div>
      </main>
    );
  }

  return (
    <main className="scAll">
      <header className="scAll__hero">
        <button
          type="button"
          className="scAll__back"
          onClick={() => navigate(-1)}
          aria-label="Назад"
        >
          <ArrowLeft
            size={22}
            strokeWidth={2.5}
          />
        </button>

        <div>
          <span>
            Бізнес-каталог
          </span>

          <h1>{businessName}</h1>

          <p>
            {services.length} послуг
            у каталозі
          </p>
        </div>
      </header>

      <section className="scAll__filters">
        <label className="scAll__search">
          <Search
            size={18}
            strokeWidth={2.5}
          />

          <input
            value={query}
            onChange={(event) =>
              setQuery(
                event.target.value
              )
            }
            placeholder="Пошук послуг"
          />
        </label>

        <div className="scAll__selects">
          <label>
            <SlidersHorizontal
              size={16}
              strokeWidth={2.5}
            />

            <select
              value={sort}
              onChange={(event) =>
                setSort(
                  event.target.value
                )
              }
            >
              <option value="newest">
                Нові спочатку
              </option>

              <option value="oldest">
                Старі спочатку
              </option>

              <option value="priceAsc">
                Дешевші спочатку
              </option>

              <option value="priceDesc">
                Дорожчі спочатку
              </option>
            </select>
          </label>

          <label>
            <select
              value={priceType}
              onChange={(event) =>
                setPriceType(
                  event.target.value
                )
              }
            >
              <option value="all">
                Усі послуги
              </option>

              <option value="withPrice">
                З ціною
              </option>

              <option value="noPrice">
                Без ціни
              </option>
            </select>
          </label>
        </div>
      </section>

      {actionError && (
        <div className="scAll__actionError">{actionError}</div>
      )}

      {filteredServices.length === 0 ? (
        <section className="scAll__empty">
          <strong>
            Послуг не знайдено
          </strong>

          <p>
            Спробуй змінити пошук або
            фільтри.
          </p>
        </section>
      ) : (
        <section className="scAll__grid">
          {filteredServices.map(
            (item) => {
              const serviceId =
                item.listingId ||
                item.id;

              const photo =
                item.mainPhoto ||
                item.photos?.[0] ||
                "";

              const hidden =
                (item.status || "active") ===
                "hidden";

              const isBusy =
                busyId === serviceId;

              return (
                <article
                  key={serviceId}
                  className={`scAllCard${
                    hidden
                      ? " scAllCard--hidden"
                      : ""
                  }`}
                >
                  <button
                    type="button"
                    className="scAllCard__image"
                    onClick={() =>
                      openServiceDetails(
                        item
                      )
                    }
                    aria-label={`Відкрити послугу: ${
                      item.title ||
                      "Послуга"
                    }`}
                  >
                    {photo ? (
                      <img
                        src={photoVariant(photo, "sm")}
                        alt={
                          item.title ||
                          "Послуга"
                        }
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <span>
                        {String(
                          item.title ||
                            "П"
                        )
                          .slice(0, 1)
                          .toUpperCase()}
                      </span>
                    )}
                    {hidden && (
                      <span className="scAllCard__hiddenBadge">
                        Приховано
                      </span>
                    )}
                  </button>

                  <div className="scAllCard__body">
                    <div className="scAllCard__info">
                      <strong>
                        {item.title ||
                          "Послуга"}
                      </strong>

                      <em>
                        {item.duration ||
                          "Тривалість не вказана"}
                      </em>
                    </div>

                    <b className="scAllCard__price">
                      {formatPrice(item)}
                    </b>

                    <div className="scAllCard__actions">
                      <button
                        type="button"
                        className="scAllCard__detailsButton"
                        onClick={() =>
                          openServiceDetails(
                            item
                          )
                        }
                      >
                        <ExternalLink
                          size={15}
                          strokeWidth={2.5}
                        />

                        Детальніше
                      </button>

                      <button
                        type="button"
                        className="scAllCard__bookButton"
                        onClick={() =>
                          openBooking(item)
                        }
                      >
                        <CalendarDays
                          size={15}
                          strokeWidth={2.5}
                        />

                        Записатись
                      </button>
                    </div>

                    {isOwnProfile && (
                      <div className="scAllCard__ownerActions">
                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={() =>
                            setEditingItem(item)
                          }
                        >
                          Редагувати
                        </button>
                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={() =>
                            toggleHidden(item)
                          }
                        >
                          {hidden
                            ? "Показати"
                            : "Приховати"}
                        </button>
                        <button
                          type="button"
                          className="scAllCard__delete"
                          disabled={isBusy}
                          onClick={() =>
                            setConfirmDeleteItem(
                              item
                            )
                          }
                        >
                          Видалити
                        </button>
                      </div>
                    )}
                  </div>
                </article>
              );
            }
          )}
        </section>
      )}

      {bookingModalOpen && (
        <div
          className="scBookingModal"
          role="dialog"
          aria-modal="true"
          aria-label="Онлайн-запис"
        >
          <button
            type="button"
            className="scBookingModal__backdrop"
            onClick={closeBooking}
            aria-label="Закрити вікно запису"
          />

          <section className="scBookingModal__panel">
            <header className="scBookingModal__header">
              <div>
                <span>
                  Онлайн-запис
                </span>

                <strong>
                  {selectedBookingService
                    ?.title ||
                    "Оберіть час"}
                </strong>
              </div>

              <button
                type="button"
                className="scBookingModal__close"
                onClick={closeBooking}
                aria-label="Закрити"
              >
                <X
                  size={21}
                  strokeWidth={2.5}
                />
              </button>
            </header>

            <div className="scBookingModal__content">
              <BookingPublic
                key={
                  selectedBookingService
                    ?.listingId ||
                  selectedBookingService?.id
                }
                ownerId={
                  viewedUserId
                }
                services={services}
                initialService={
                  selectedBookingService
                }
              />
            </div>
          </section>
        </div>
      )}

      {isOwnProfile && (
        <BusinessModuleCreate
          open={!!editingItem}
          moduleType="services"
          editItem={editingItem}
          onClose={closeEditModal}
          onSubmit={handleEditSaved}
        />
      )}

      {confirmDeleteItem && (
        <div
          className="scAll__confirmOverlay"
          onMouseDown={() => setConfirmDeleteItem(null)}
        >
          <div
            className="scAll__confirmModal"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <p>
              Ви впевнені, що хочете видалити цей блок? Дані буде видалено без
              можливості відновлення.
            </p>
            <div className="scAll__confirmActions">
              <button
                type="button"
                onClick={() => setConfirmDeleteItem(null)}
              >
                Скасувати
              </button>
              <button
                type="button"
                className="scAll__confirmDelete"
                disabled={
                  busyId ===
                  (confirmDeleteItem.listingId || confirmDeleteItem.id)
                }
                onClick={confirmDelete}
              >
                Видалити
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}